import { generateUUID } from './generateUUID';

/**
 * Client-Schluessel je Absende-Absicht — Variante C des Wiederholschutzes
 * (Entscheid Alex 14.08.2026, docs/tasks/buchungskern-haertung.md Teil 2).
 *
 * WOFUER. Der Buchungspfad im Backend erkennt eine Doppel-Absendung an einem
 * Idempotenz-Schluessel. Fuer FREMDE Clients (oeffentliche Seite,
 * Callcenter-API) leitet der Server ihn deterministisch aus dem Request ab.
 * Fuer UNSERE eigenen Clients ist eine Client-UUID praeziser: nur das FE weiss,
 * ob zwei Requests dieselbe Absicht sind ("ich habe nochmal geklickt") oder
 * zwei gewollte Buchungen.
 *
 * DIE REGEL, an der alles haengt: derselbe Schluessel bleibt, solange die
 * Absicht besteht — ueber Fehlversuche hinweg. Erst ein ERFOLG beendet sie.
 * Genau deshalb schuetzt der Mechanismus auch den Fall, fuer den es ihn
 * eigentlich gibt: die Antwort geht unterwegs verloren, der Nutzer klickt
 * erneut, und die zweite Anfrage traegt denselben Schluessel — der Server
 * liefert die erste Antwort statt einer zweiten Buchung.
 *
 * Wuerde der Schluessel bei jedem Klick neu erzeugt, waere er wertlos; wuerde
 * er nach einem Fehler verworfen, schuetzte er nur den Doppelklick und nicht
 * den Wiederholversuch.
 *
 * 🔥 WARUM DAS TROTZDEM SICHER IST, wenn der Nutzer zwischendurch den Slot
 * wechselt: der Server legt neben dem Ergebnis den HASH des Vorgangs-Inhalts
 * ab und liefert die alte Antwort nur bei gleichem Schluessel UND gleichem
 * Inhalt. Eine geaenderte Buchung laeuft normal durch, auch wenn dieser
 * Schluessel noch derselbe ist. Das FE muss den Wechsel also nicht selbst
 * erkennen — diese Absicherung liegt bewusst auf der Serverseite, wo sie
 * niemand vergessen kann.
 */

/** Buchungswege mit eigenem Client. Der oeffentliche Weg fehlt bewusst. */
export type AbsendeKanal = 'internal' | 'wizard' | 'self';

const offeneAbsichten: Partial<Record<AbsendeKanal, string>> = {};

/**
 * Schluessel der laufenden Absicht — legt beim ersten Aufruf einen an und
 * liefert danach denselben, bis {@link absichtAbschliessen} ihn beendet.
 */
export const absichtSchluessel = (kanal: AbsendeKanal): string => {
  const vorhanden = offeneAbsichten[kanal];
  if (vorhanden) return vorhanden;

  const neu = generateUUID();
  offeneAbsichten[kanal] = neu;
  return neu;
};

/**
 * Beendet die Absicht. NUR nach einem Erfolg aufrufen — nach einem Fehler
 * muss der Schluessel stehen bleiben, sonst traegt der Wiederholversuch einen
 * neuen und der Server kann ihn nicht als denselben Vorgang erkennen.
 */
export const absichtAbschliessen = (kanal: AbsendeKanal): void => {
  delete offeneAbsichten[kanal];
};

/** Nur fuer Tests: alle offenen Absichten verwerfen. */
export const absichtenZuruecksetzen = (): void => {
  (Object.keys(offeneAbsichten) as AbsendeKanal[]).forEach((k) => delete offeneAbsichten[k]);
};
